import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import { useCallContext } from '@gem/call/CallView/CallView.context';
import { useTranslation } from '@gem/modules/i18n';

interface ICallLimitsParticipantsToastPresenter {
  call: Call;
  isSidebarLimitReachedVisible: boolean;
  isSidebarLimitReminderVisible: boolean;
  maxParticipants: number;
  showParticipantsLimitRestrictedBanner: boolean;
  opponentsLength: number;
  reminderText: string[] | null;
  timeout: ReturnType<typeof setInterval> | null;
  isCallTimeLimitsVisible: boolean;
  onReminderParticipantsClick(): void;
  onReminderTimeClick(): void;
  clearReminderText(): void;
  setReminderText(): void;
}

export const useCallLimitsParticipantsToastPresenter =
  (): ICallLimitsParticipantsToastPresenter => {
    const { t } = useTranslation();
    const context = useCallContext();

    const presenter = useLocalObservable<ICallLimitsParticipantsToastPresenter>(() => ({
      timeout: null,
      reminderText: null,
      get call(): Call {
        return context.call;
      },

      get opponentsLength(): number {
        return presenter.call.opponents.opponentsWithoutScreenSharing.length;
      },

      get showParticipantsLimitRestrictedBanner(): boolean {
        return presenter.call.uiFeatures.showParticipantsRestrictedLimitReachedBanner;
      },

      get isSidebarLimitReminderVisible(): boolean {
        return (
          presenter.call.uiFeatures.showParticipantsLimitWillReachSoonBanner &&
          !!presenter.call.callLimits.maxParticipantsReminder &&
          presenter.call.callLimits.maxParticipantsReminder <= presenter.opponentsLength
        );
      },

      get maxParticipants(): number {
        return presenter.call.callLimits.maxParticipants;
      },

      get isSidebarLimitReachedVisible(): boolean {
        return presenter.maxParticipants <= presenter.opponentsLength;
      },

      clearReminderText(): void {
        presenter.timeout = setTimeout(() => {
          presenter.reminderText = null;
        }, 10000);
      },

      setReminderText(): void {
        if (
          presenter.isSidebarLimitReachedVisible &&
          !presenter.showParticipantsLimitRestrictedBanner
        ) {
          presenter.reminderText = [
            t('premium_feature_extended_size_calls_total_limit_reached', {
              0: presenter.maxParticipants,
            }),
          ];
        } else if (
          presenter.isSidebarLimitReachedVisible &&
          presenter.showParticipantsLimitRestrictedBanner
        ) {
          presenter.reminderText = [
            t('premium_error_extended_size_calls_limit_reached', {
              0: presenter.maxParticipants,
            }),

            t('premium_feature_extended_size_calls_available_in_premium', {
              0: presenter.maxParticipants,
            }),
          ];
        } else if (presenter.isSidebarLimitReminderVisible) {
          presenter.reminderText = [
            t('premium_feature_extended_size_calls_spots_left', {
              0: presenter.maxParticipants - presenter.opponentsLength,
            }),
            t('premium_feature_extended_size_calls_available_in_premium', {
              0: presenter.maxParticipants,
            }),
          ];
        }

        if (presenter.reminderText && presenter.timeout) {
          clearTimeout(presenter.timeout);
        }
      },

      onReminderParticipantsClick(): void {
        if (
          presenter.isSidebarLimitReachedVisible &&
          !presenter.showParticipantsLimitRestrictedBanner
        ) {
          return;
        }

        if (context.callController.handlers.onReminderParticipantsClick) {
          context.callController.handlers.onReminderParticipantsClick();
        }
      },

      onReminderTimeClick(): void {
        if (context.callController.handlers.onReminderTimeClick) {
          context.callController.handlers.onReminderTimeClick();
        }
      },

      get isCallTimeLimitsVisible(): boolean {
        return (
          !presenter.call.isCalling &&
          presenter.call.uiFeatures.showCallWillEndSoonBanner &&
          !!presenter.call.callLimits.maxDurationReminder &&
          !!presenter.call.callLimits.maxDuration &&
          presenter.call.callLimits.maxDuration - presenter.call.duration <
            presenter.call.callLimits.maxDurationReminder
        );
      },
    }));

    useEffect(() => {
      if (presenter.isSidebarLimitReminderVisible || presenter.isSidebarLimitReachedVisible) {
        presenter.setReminderText();
      } else if (
        !presenter.isSidebarLimitReachedVisible &&
        !presenter.isSidebarLimitReminderVisible
      ) {
        presenter.clearReminderText();
      }
    }, [presenter.isSidebarLimitReminderVisible, presenter.isSidebarLimitReachedVisible]);
    return presenter;
  };
